/**
 * Event reducer (§4.3): state = effectiveEvents(events).reduce(applyEvent, initialState).
 */
import type { ClockState } from '../clock/clock';
import { adjust, createClock, releaseShot, resetShot, start, stop } from '../clock/clock';
import type { BreakTimer } from '../clock/breakTimer';
import { adjustBreak, pauseBreak, resumeBreak, startBreak } from '../clock/breakTimer';
import type { GameEvent } from '../events/types';
import type { MatchInfo } from '../match/match';
import { withMatchDetails } from '../match/match';
import { effectiveEvents, sortEvents } from '../events/factory';
import type { RuleSet } from '../rules/ruleset';
import { cloneRules, isOvertime, periodLengthMs } from '../rules/ruleset';
import type { PerSide, TeamSide, Teams } from '../types';
import { otherSide } from '../types';
import { freeThrowThen } from '../fouls/fouls';
import type { TeamFoulStatus } from './derived';
import { teamFoulStatus, timeoutsRemaining } from './derived';
import type { PlayerFoulTally } from '../fouls/fouls';
import {
  addPlayerFoul,
  countsAsTeamFoul,
  emptyPlayerFoulTally,
  freeThrowsAwarded,
} from '../fouls/fouls';

/** A throw-in restarts the clock automatically after this long; free throws wait for a tap. */
export const AUTO_RESUME_MS = 5000;

/**
 * The clock is waiting to resume after a foul (§5.2). `auto` restarts by itself at `at`
 * (wall ms); `manual` waits for the scorer.
 */
export type ClockResume = { mode: 'auto'; at: number } | { mode: 'manual' };

/** pregame = created, period 1 not started; live = period running; break = between periods. */
export type GameStatus = 'pregame' | 'live' | 'break' | 'final';

export interface PeriodScore {
  periodNo: number;
  home: number;
  away: number;
}

export interface GameState {
  gameId: string;
  rules: RuleSet;
  teams: Teams;
  status: GameStatus;
  /** Current period (0 before period 1 starts; stays on the ended period during a break). */
  period: number;
  isOvertime: boolean;
  score: PerSide<number>;
  /** Index = periodNo - 1. */
  periodScores: PeriodScore[];
  /**
   * Team fouls per period (index = periodNo - 1), without carry-over. Only fouls that count
   * as team fouls (see countsAsTeamFoul); bench fouls and rule-excluded types are left out.
   */
  teamFoulsByPeriod: PerSide<number>[];
  /** Every TEAM_FOUL event per period, including bench and non-counting fouls. */
  allFoulsByPeriod: PerSide<number>[];
  /** Per-player foul tallies keyed by jersey number. */
  playerFouls: PerSide<Record<string, PlayerFoulTally>>;
  /** Raw timeouts per period (index = periodNo - 1). */
  timeoutsByPeriod: PerSide<number>[];
  /** Foul / penalty status for the current period (carry-over applied). */
  fouls: PerSide<TeamFoulStatus>;
  /** Timeouts remaining for the current period. */
  timeoutsRemaining: PerSide<number>;
  clock: ClockState;
  /** The clock is waiting to resume after a foul; null otherwise. */
  clockResume: ClockResume | null;
  /** Team with the ball (possession pointer); null until known. */
  possession: TeamSide | null;
  /** Team the alternating-possession arrow points to; null until the opening jump ball. */
  arrow: TeamSide | null;
  /** Timeout or between-period break timer; null when no break is being timed. */
  breakTimer: BreakTimer | null;
  /** Wall ms of the first clock start (match start time); null before tip-off. */
  startedAt: number | null;
  /** Wall ms when the game ended; null until then. */
  endedAt: number | null;
  /** Match number and tournament details; null for games created before v3. */
  match: MatchInfo | null;
  /** Jersey numbers on court, for teams whose five are followed; null until they are chosen. */
  onCourt: PerSide<readonly string[] | null>;
  /** The coming period each team has marked its starting five for; null until it has. */
  startersMarkedFor: PerSide<number | null>;
  /** Highest seq applied (including voided events seen by deriveState). */
  lastSeq: number;
}

function zero(): PerSide<number> {
  return { home: 0, away: 0 };
}

function withSummary(state: GameState): GameState {
  const { rules, period } = state;
  return {
    ...state,
    isOvertime: isOvertime(rules, period),
    fouls: {
      home: teamFoulStatus(rules, state.teamFoulsByPeriod, period, 'home'),
      away: teamFoulStatus(rules, state.teamFoulsByPeriod, period, 'away'),
    },
    timeoutsRemaining: {
      home: timeoutsRemaining(rules, state.timeoutsByPeriod, period, 'home'),
      away: timeoutsRemaining(rules, state.timeoutsByPeriod, period, 'away'),
    },
  };
}

export function initialState(rules: RuleSet, teams: Teams, gameId = ''): GameState {
  return withSummary({
    gameId,
    rules: cloneRules(rules),
    teams: { home: { ...teams.home }, away: { ...teams.away } },
    status: 'pregame',
    period: 0,
    isOvertime: false,
    score: zero(),
    periodScores: [],
    teamFoulsByPeriod: [],
    allFoulsByPeriod: [],
    playerFouls: { home: {}, away: {} },
    onCourt: { home: null, away: null },
    startersMarkedFor: { home: null, away: null },
    timeoutsByPeriod: [],
    fouls: {
      home: { count: 0, penaltyAt: 0, inPenalty: false, foulsToGive: 0 },
      away: { count: 0, penaltyAt: 0, inPenalty: false, foulsToGive: 0 },
    },
    timeoutsRemaining: zero(),
    clock: createClock(periodLengthMs(rules, 1), rules.shotClock),
    clockResume: null,
    possession: null,
    arrow: null,
    breakTimer: null,
    startedAt: null,
    endedAt: null,
    match: null,
    lastSeq: 0,
  });
}

/** True when this SCORE ends a possession: a field goal, or the last free throw of a trip. */
function endsPossession(payload: {
  freeThrow?: boolean;
  lastFreeThrow?: boolean;
  andOne?: boolean;
  points: number;
}) {
  if (payload.andOne === true) return false;
  // A point can only come from a free throw (a plain +1 tap ends nothing).
  return payload.freeThrow === true ? payload.lastFreeThrow === true : payload.points >= 2;
}

/** Grow the per-period arrays so that index periodNo - 1 exists. */
function ensurePeriod(state: GameState, periodNo: number): GameState {
  if (periodNo < 1 || state.periodScores.length >= periodNo) return state;
  const periodScores = state.periodScores.slice();
  const teamFoulsByPeriod = state.teamFoulsByPeriod.slice();
  const allFoulsByPeriod = state.allFoulsByPeriod.slice();
  const timeoutsByPeriod = state.timeoutsByPeriod.slice();
  for (let p = periodScores.length + 1; p <= periodNo; p++) {
    periodScores.push({ periodNo: p, home: 0, away: 0 });
    teamFoulsByPeriod.push(zero());
    allFoulsByPeriod.push(zero());
    timeoutsByPeriod.push(zero());
  }
  return { ...state, periodScores, teamFoulsByPeriod, allFoulsByPeriod, timeoutsByPeriod };
}

function bump(
  list: PerSide<number>[],
  periodNo: number,
  team: TeamSide,
  by: number,
): PerSide<number>[] {
  return list.map((row, i) => (i === periodNo - 1 ? { ...row, [team]: row[team] + by } : row));
}

/** Period an event is attributed to (events before period 1 count toward period 1). */
function attributionPeriod(event: GameEvent): number {
  return Math.max(1, event.periodNo);
}

/**
 * Applies one (non-voided) event. EVENT_VOIDED is a no-op here: undo is handled by
 * deriveState(), which drops voided events before folding.
 */
export function applyEvent(state: GameState, event: GameEvent): GameState {
  const next = applyCore(state, event);
  return withSummary({ ...next, lastSeq: Math.max(next.lastSeq, event.seq) });
}

function applyCore(state: GameState, event: GameEvent): GameState {
  switch (event.type) {
    case 'GAME_CREATED':
      return {
        ...initialState(event.payload.rules, event.payload.teams, event.gameId),
        match: event.payload.match ?? null,
      };

    case 'PERIOD_STARTED': {
      const p = event.payload.periodNo;
      const s = ensurePeriod(state, p);
      return {
        ...s,
        status: 'live',
        period: p,
        clockResume: null,
        // Art. 12.6.3-12.6.4: the arrow team starts the quarter, then the arrow reverses.
        ...(p > 1 && s.arrow !== null ? { possession: s.arrow, arrow: otherSide(s.arrow) } : {}),
        clock: createClock(periodLengthMs(s.rules, p), s.rules.shotClock),
      };
    }

    case 'PERIOD_ENDED':
      return {
        ...ensurePeriod(state, event.payload.periodNo),
        status: 'break',
        period: event.payload.periodNo,
        clockResume: null,
        clock: stop(state.clock, event.at, event.clockMs),
      };

    case 'CLOCK_STARTED':
      return {
        ...state,
        startedAt: state.startedAt ?? event.at,
        clockResume: null,
        clock: start(
          { ...state.clock, runningSince: null, remainingMsAtStart: event.payload.remainingMs },
          event.at,
        ),
      };

    case 'CLOCK_STOPPED':
      return { ...state, clock: stop(state.clock, event.at, event.payload.remainingMs) };

    case 'CLOCK_ADJUSTED':
      return { ...state, clock: adjust(state.clock, event.payload.remainingMs, event.at) };

    case 'SCORE': {
      const p = attributionPeriod(event);
      const s = ensurePeriod(state, p);
      const { team, points } = event.payload;
      // §5.2: after a basket the ball goes to the other team with a full shot clock.
      const turnover = s.rules.shotClock.resetOnScore === true && endsPossession(event.payload);
      return {
        ...s,
        ...(turnover
          ? {
              possession: otherSide(team),
              clock: resetShot(
                s.clock,
                s.rules.shotClock.fullSec,
                event.at,
                s.rules.shotClock.holdAfterScore === true,
              ),
            }
          : {}),
        score: { ...s.score, [team]: s.score[team] + points },
        periodScores: s.periodScores.map((row) =>
          row.periodNo === p ? { ...row, [team]: row[team] + points } : row,
        ),
      };
    }

    case 'TEAM_FOUL': {
      // Art. 42.1.2: a foul in an interval belongs to the following quarter.
      const p = state.status === 'break' ? state.period + 1 : attributionPeriod(event);
      const s = ensurePeriod(state, p);
      const d = event.payload;
      const counts = countsAsTeamFoul(s.rules, d);
      let playerFouls = s.playerFouls;
      if (d.bench !== true && d.playerNumber !== undefined) {
        const teamTallies = s.playerFouls[d.team];
        const prev = teamTallies[d.playerNumber] ?? emptyPlayerFoulTally(s.rules, d.playerNumber);
        playerFouls = {
          ...s.playerFouls,
          [d.team]: { ...teamTallies, [d.playerNumber]: addPlayerFoul(s.rules, prev, d.foulType) },
        };
      }
      const clockResume: ClockResume | null =
        d.stoppedClock !== true
          ? s.clockResume
          : freeThrowsAwarded(d, state.fouls[d.team].inPenalty) === 0
            ? { mode: 'auto', at: event.at + AUTO_RESUME_MS }
            : { mode: 'manual' };
      // A player's foul gives the ball to the other team, by throw-in or free throws.
      const fouledTeamGetsBall = d.bench !== true && freeThrowThen(d) !== 'resume';
      return {
        ...s,
        clockResume,
        ...(fouledTeamGetsBall ? { possession: otherSide(d.team) } : {}),
        teamFoulsByPeriod: counts ? bump(s.teamFoulsByPeriod, p, d.team, 1) : s.teamFoulsByPeriod,
        allFoulsByPeriod: bump(s.allFoulsByPeriod, p, d.team, 1),
        playerFouls,
      };
    }

    case 'FREE_THROW_MISSED':
      return state;

    case 'TIMEOUT': {
      const p = attributionPeriod(event);
      const s = ensurePeriod(state, p);
      return {
        ...s,
        clockResume: null,
        timeoutsByPeriod: bump(s.timeoutsByPeriod, p, event.payload.team, 1),
      };
    }

    case 'SHOT_CLOCK_RESET':
      return { ...state, clock: resetShot(state.clock, event.payload.seconds, event.at) };

    case 'REBOUND': {
      const sc = state.rules.shotClock;
      return {
        ...state,
        possession: event.payload.team,
        clock: resetShot(state.clock, event.payload.offensive ? sc.resetSec : sc.fullSec, event.at),
      };
    }

    case 'SHOT_CLOCK_STARTED':
      return { ...state, clock: releaseShot(state.clock, event.at) };

    case 'CLOCK_RESUME_HELD':
      return state.clockResume === null ? state : { ...state, clockResume: { mode: 'manual' } };

    case 'POSSESSION_SET': {
      const { team, arrow } = event.payload;
      return {
        ...state,
        ...(team !== undefined ? { possession: team } : {}),
        ...(arrow !== undefined ? { arrow } : {}),
      };
    }

    case 'BREAK_STARTED':
      return {
        ...state,
        breakTimer: startBreak(event.payload.kind, event.payload.remainingMs, event.at),
      };

    case 'BREAK_PAUSED':
      return state.breakTimer === null
        ? state
        : {
            ...state,
            breakTimer: pauseBreak(state.breakTimer, event.at, event.payload.remainingMs),
          };

    case 'BREAK_RESUMED':
      return state.breakTimer === null
        ? state
        : {
            ...state,
            breakTimer: resumeBreak(state.breakTimer, event.at, event.payload.remainingMs),
          };

    case 'BREAK_ADJUSTED':
      return state.breakTimer === null
        ? state
        : {
            ...state,
            breakTimer: adjustBreak(state.breakTimer, event.payload.remainingMs, event.at),
          };

    case 'BREAK_ENDED':
      return { ...state, breakTimer: null };

    case 'GAME_INFO_UPDATED': {
      const jerseys = event.payload.jerseys ?? {};
      const teams = { ...state.teams };
      for (const side of ['home', 'away'] as const) {
        const j = jerseys[side];
        if (j === undefined) continue;
        // The update replaces the jersey: without `kit` the colour is from neither set.
        const { kit: _oldKit, ...team } = teams[side];
        teams[side] = { ...team, color: j.color, ...(j.kit !== undefined ? { kit: j.kit } : {}) };
      }
      const { details } = event.payload;
      return {
        ...state,
        teams,
        ...(details !== undefined && state.match !== null
          ? { match: withMatchDetails(state.match, details) }
          : {}),
      };
    }

    case 'LINEUP_SET':
      return {
        ...state,
        onCourt: { ...state.onCourt, [event.payload.team]: [...event.payload.numbers] },
        ...(event.payload.forPeriod !== undefined
          ? {
              startersMarkedFor: {
                ...state.startersMarkedFor,
                [event.payload.team]: event.payload.forPeriod,
              },
            }
          : {}),
      };

    case 'PLAYER_REMOVED': {
      const { team, number } = event.payload;
      return {
        ...state,
        onCourt: {
          ...state.onCourt,
          [team]: (state.onCourt[team] ?? []).filter((n) => n !== number),
        },
      };
    }

    case 'SUBSTITUTION': {
      const { team, out, in: into } = event.payload;
      return {
        ...state,
        onCourt: {
          ...state.onCourt,
          [team]: (state.onCourt[team] ?? []).map((n) => (n === out ? into : n)),
        },
      };
    }

    case 'EVENT_VOIDED':
      return state;

    case 'GAME_ENDED':
      return {
        ...state,
        status: 'final',
        endedAt: event.at,
        clockResume: null,
        breakTimer: null,
        clock: stop(state.clock, event.at, event.clockMs),
      };

    default:
      // Unknown (newer-schema) event types are ignored rather than crashing a replay.
      return state;
  }
}

/**
 * Builds the state from the full event log (any order; sorted by seq). Requires a
 * GAME_CREATED event; without one, `fallback` rules/teams are used if given.
 */
export function deriveState(
  events: readonly GameEvent[],
  fallback?: { rules: RuleSet; teams: Teams; gameId?: string },
): GameState {
  const sorted = sortEvents(events);
  const created = sorted.find((e) => e.type === 'GAME_CREATED');
  let state: GameState;
  if (created !== undefined && created.type === 'GAME_CREATED') {
    state = initialState(created.payload.rules, created.payload.teams, created.gameId);
  } else if (fallback !== undefined) {
    state = initialState(fallback.rules, fallback.teams, fallback.gameId ?? '');
  } else {
    throw new Error('deriveState: no GAME_CREATED event and no fallback');
  }
  for (const e of effectiveEvents(sorted)) state = applyEvent(state, e);
  const lastSeq = sorted.length > 0 ? (sorted[sorted.length - 1]?.seq ?? 0) : 0;
  return { ...state, lastSeq: Math.max(state.lastSeq, lastSeq) };
}

/**
 * Incremental update after appending `newEvents` to `allEvents` (which already contains them).
 * Folds the new events directly, or fully re-derives when one of them voids something.
 */
export function advanceState(
  state: GameState,
  allEvents: readonly GameEvent[],
  newEvents: readonly GameEvent[],
): GameState {
  if (newEvents.some((e) => e.type === 'EVENT_VOIDED')) return deriveState(allEvents);
  let s = state;
  for (const e of sortEvents(newEvents)) s = applyEvent(s, e);
  return s;
}
